import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { schema, FormData } from "./Profile";
import { toast } from "react-toastify";
import { useSearchParams } from "next/navigation";
import { useAuth } from "@/store/auth";
import ImageEditor from "./ImageEditor";
import {
  Camera,
  LandmarkIcon,
  Trash2,
  UploadCloud,
  CreditCard,
  Pencil,
} from "lucide-react";
import {
  useAddBankInfo,
  useAddCardInfo,
  useGetBankDetails,
  useGetUpdateUserDoc,
  useUnAssignedBookingMutation,
  useUpateUserProfile,
  useUpdateUserDoc,
  useGetCardDetails,
  useGetBankDetailsById,
  useUpdateBankDetails,
  useUpdateCardDetails,
} from "@/hooks/useCommon";
import BookingTable from "./common/Table";

const ProfileBankSchema = z.object({
  bank_name: z.string().min(1, "Bank Name is required"),
  account_holder_name: z.string().min(1, "Account Holder Name is required"),
  account_no: z.string().min(1, "Account No. is required"),
  branch: z.string().min(1, "Branch is required"),
  address: z.string().min(1, "Address is required"),
  ifsc_code: z.string().min(1, "IFSC Code is required"),
  upi_id: z
    .string()
    .trim()
    .min(5, "UPI ID must be at least 5 characters long")
    .regex(
      /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/,
      "Enter a valid UPI ID (e.g., name@bank)"
    ),
});

// Card schema matching database structure
const CardSchema = z.object({
  card_holder_name: z.string().min(1, "Cardholder name is required"),
  card_number: z
    .string()
    .min(12, "Card number must be at least 12 digits")
    .max(20, "Card number cannot exceed 20 digits"),
  payment_method: z.enum(["0", "1"], {
    errorMap: () => ({ message: "Please select card type" }),
  }),
  expiry_date: z.string().min(1, "Expiry date is required"),
  cvv: z
    .string()
    .min(3, "CVV must be at least 3 digits")
    .max(4, "CVV cannot exceed 4 digits")
    .regex(/^\d+$/, "CVV must contain only digits"),
});

type ProfileBankData = z.infer<typeof ProfileBankSchema>;
type CardData = z.infer<typeof CardSchema>;

interface bankDetail {
  id: number;
  itinerary_id: number;
  bank_name: string;
  account_holder_name: string;
  account_no: string;
  ifsc_code: string;
  branch: string;
  address: string;
}

interface cardDetail {
  id: number;
  card_number: string;
  card_holder_name: string;
  payment_method: number;
  expiry_date: string;
  cvv: string;
  created_at: string;
  updated_at: string;
  user_id: number;
}

interface Column {
  key: string;
  header: string;
  sortable?: boolean;
  filterable?: boolean;
  filterType?: any;
  render?: (value: any, row?: any) => React.ReactNode;
  hidden?: boolean;
}

export default function UserBankDetailForm() {
  const {
    register,
    handleSubmit,
    setValue,
    control,
    reset,
    resetField,
    formState: { errors },
  } = useForm<ProfileBankData>({
    resolver: zodResolver(ProfileBankSchema),
  });

  const [cardDetails, setCardDetails] = useState<CardData[]>([]);
  const [bankProof, setBankProof] = useState<string | null>(null);
  const [bankQr, setBankQr] = useState<string | null>(null);
  const [bankId, setBankId] = useState<number | null>(null);
  const [card, setCard] = useState(null);
  const [cardId,setCardId] = useState(null);

  const profilePath = process.env.NEXT_PUBLIC_API_PIC_URL;
  const b2bUrl = process.env.NEXT_PUBLIC_B2B_URL;

  const searchParams = useSearchParams();
  const user_id = searchParams.get("user_id");
  const path = searchParams.get("type");
  const { user: authUser } = useAuth();
  const userId = path ? user_id : authUser?.id ?? -1;

  const { data: bankDetails, refetch: refetchBankDetails } =
    useGetBankDetails();
  const { data: cardDetailsData, refetch: refetchCardDetails } =
    useGetCardDetails(userId as number);
  const { mutate: unAssignedBookingMutate, data: getUnAssignedBookingData } =
    useUnAssignedBookingMutation();

  const {
    data: bankDetail,
    refetch: refetchSingleBankDetail,
    isFetching,
  } = useGetBankDetailsById(bankId || undefined); // will only trigger if bankId is set
  const { mutate: addBankInfo, isSuccess } = useAddBankInfo();
  const { mutate: updateBankInfo } = useUpdateBankDetails(bankId ?? 0);
  const { mutate: updateCardInfo } = useUpdateCardDetails();
  const { data: docData } = useGetUpdateUserDoc();
  const {
    mutate: addCardInfo,
    isSuccess: cardSuccess,
    isError: cardErr,
  } = useAddCardInfo();

  function getDocumentPath(docs: any[], typeId: number, basePath?: string) {
    const doc = docs?.find((d) => d.document_type_id === typeId);
    if (!doc || !doc.doc_file_upload) return "";

    const fileParts = doc.doc_file_upload.split("/");
    const folderPath = fileParts.slice(0, -1).join("/");
    const fileName = fileParts[fileParts.length - 1] || "";

    return `${basePath}${folderPath}/${encodeURIComponent(fileName)}`;
  }

  const bankProofPicture = getDocumentPath(docData, 2, profilePath);
  const { mutate: uploadDoc, data: uploadedData } = useUpdateUserDoc(
    Number(authUser?.id)
  );
  const handleCardUpdate = (data: any) => {
    setCard(data); 
    setCardId(data?.id);

    setCardDetails((prev) => [
      {
        card_holder_name: data?.card_holder_name || "",
        card_number: data?.card_number || "",
        payment_method: "0",
        expiry_date: data?.expiry_date?.slice(0, 7) || "",
        cvv: data?.cvv || "",
      },
    ]);
  };

  // Bank details data
  const unAssignedData: bankDetail[] = bankDetails?.map(
    (item: any, index: number) => ({
      id: item?.bank_id,
      bank_name: item?.name,
      account_holder_name: item?.ac_holder_name,
      account_no: item?.ac_no,
      ifsc_code: item?.ifsc_code,
      branch: item?.branch,
      address: item?.address,
    })
  );

  // Card details data
  const cardData: cardDetail[] =
    cardDetailsData?.cards?.map((card: any) => ({
      id: card?.id,
      card_number: card?.card_number,
      card_holder_name: card?.card_holder_name,
      payment_method: card?.payment_method || 0,
      expiry_date: card?.expiry_date,
      cvv: card?.cvv,
      created_at: card?.created_at,
      updated_at: card?.updated_at,
      user_id: card?.user_id,
    })) || [];

  const handleBankDetailsEdit = (id: number) => {
    setBankId(id);
    setTimeout(() => {
      refetchSingleBankDetail(id);
    }, 0);
  };

  useEffect(() => {
    if (bankDetail) {
      setValue("bank_name", bankDetail.name || "");
      setValue("account_holder_name", bankDetail.ac_holder_name || "");
      setValue("account_no", bankDetail.ac_no || "");
      setValue("branch", bankDetail.branch || "");
      setValue("address", bankDetail.address || "");
      setValue("ifsc_code", bankDetail.ifsc_code || "");
      setValue("upi_id", bankDetail.upi_id || "");
      setBankProof(bankDetail.bank_proof);
      setBankQr(bankDetail.upi_qr);
    }
  }, [bankDetail]);

  const bankDetailColumns: Column[] = [
    { key: "id", header: "ID", sortable: true, filterable: true },
    {
      key: "bank_name",
      header: "Bank Name",
      sortable: true,
      filterable: true,
    },
    {
      key: "account_holder_name",
      header: "Account Holder Name",
      sortable: true,
      filterable: true,
    },
    {
      key: "account_no",
      header: "Account No.",
      sortable: true,
      filterable: true,
      render: (_: string, row: any) => (
        <span>
          {row.account_no
            ? row.account_no.toString().replace(/.(?=.{4})/g, "*")
            : "N/A"}
        </span>
      ),
    },
    {
      key: "ifsc_code",
      header: "IFSC Code",
      sortable: true,
      filterable: true,
    },
    {
      key: "branch",
      header: "Branch",
      sortable: true,
      filterable: true,
    },
    {
      key: "address",
      header: "Address",
      sortable: true,
      filterable: true,
      render: (_: string, row: any) => (
        <div
          className="text-gray-600 truncate max-w-[120px]"
          title={row.address}
        >
          {row.address}
        </div>
      ),
    },
    {
      key: "action",
      header: "Action",
      sortable: false,
      render: (_: string, row: any) => (
        <div className="flex space-x-2">
          <button
            className="text-blue-600 hover:text-blue-800 hover:underline text-sm cursor-pointer"
            onClick={() => handleBankDetailsEdit(row.id)}
          >
            <Pencil className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  // Card details columns
  const cardDetailColumns: Column[] = [
    { key: "id", header: "ID", sortable: true, filterable: true },
    {
      key: "card_holder_name",
      header: "Card Holder Name",
      sortable: true,
      filterable: true,
    },
    {
      key: "card_number",
      header: "Card Number",
      sortable: true,
      filterable: true,
      render: (_: string, row: any) => (
        <span>
          {row.card_number
            ? row.card_number.toString().replace(/.(?=.{4})/g, "*")
            : "N/A"}
        </span>
      ),
    },
    {
      key: "payment_method",
      header: "Card Type",
      sortable: true,
      filterable: true,
      render: (value: number) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            value === 0
              ? "bg-blue-100 text-blue-800"
              : "bg-green-100 text-green-800"
          }`}
        >
          {value === 0 ? "Credit Card" : "Debit Card"}
        </span>
      ),
    },
    {
      key: "expiry_date",
      header: "Expiry Date",
      sortable: true,
      filterable: true,
      render: (value: string) => (
        <span>
          {value
            ? new Date(value).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
              })
            : "N/A"}
        </span>
      ),
    },
    {
      key: "created_at",
      header: "Added On",
      sortable: true,
      filterable: true,
      render: (value: string) => (
        <span>
          {value
            ? new Date(value).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : "N/A"}
        </span>
      ),
    },
    {
      key: "action",
      header: "Action",
      sortable: false,
      render: (_: string, row: any) => (
        <div className="flex space-x-2">
          <button
            className="text-blue-600 hover:text-blue-800 hover:underline text-sm cursor-pointer"
            onClick={() => handleCardUpdate(row)}
          >
            <Pencil className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  // Handle bank form submission
  const onSubmit = (data: ProfileBankData) => {
    const payload = {
      name: data.bank_name,
      branch: data.branch,
      address: data.address,
      ifsc_code: data.ifsc_code,
      ac_no: data.account_no,
      ac_holder_name: data.account_holder_name,
      upi_id: data.upi_id,
      bank_proof: bankProof ?? "",
      upi_image: bankQr,
    };

    if (bankDetail) {
      updateBankInfo(payload, {
        onSuccess: () => {
          toast.success("Bank details updated successfully");
          refetchBankDetails();
        },
        onError: (err) => {
          alert("Error updating bank info");
          console.error(err);
        },
      });
    } else {
      addBankInfo(payload, {
        onSuccess: () => {
          toast.success("Bank details added successfully");
          refetchBankDetails();
        },
        onError: (err) => {
          alert("Error adding bank info");
          console.error(err);
        },
      });
    }

    setBankProof(null);
    setBankQr(null);
    setValue("bank_name", "");
    setValue("account_holder_name", "");
    setValue("account_no", "");
    setValue("branch", "");
    setValue("address", "");
    setValue("ifsc_code", "");
    setValue("upi_id", "");
  };

  // Handle card form submission
  const handleCardSubmit = () => {
    if (cardDetails.length === 0) {
      toast.error("Please add at least one card");
      return;
    }

    // Validate all cards
    const validationResults = cardDetails.map((card, index) => {
      try {
        CardSchema.parse(card);
        return { valid: true, index };
      } catch (error) {
        console.log({ error });
        return {
          valid: false,
          index,
          errors:
            typeof error === "object" && error !== null && "errors" in error
              ? (error as any).errors
              : error,
        };
      }
    });

    const invalidCards = validationResults.filter((result) => !result.valid);
    if (invalidCards.length > 0) {
      invalidCards.forEach((card) => {
        toast.error(`Card ${card.index + 1} has validation errors`);
      });
      return;
    }

    // Prepare card payload according to backend expectations
    const cardsPayload = cardDetails.map((card) => ({
      card_number: card.card_number.replace(/\s/g, ""),
      card_holder_name: card.card_holder_name,
      payment_method: parseInt(card.payment_method), // Convert to number (0 or 1)
      expiry_date: card.expiry_date,
      cvv: card.cvv,
    }));

    const payload = {
      user_id: userId?.toString(),
      cards: cardsPayload,
    };

    console.log("Submitting card data:", payload); // For debugging

    if (card && cardId != 0) {
      updateCardInfo(
        {
          id: cardId, // The card's ID you want to update
          card_holder_name: payload?.cards?.[0]?.card_holder_name,
          card_number: payload?.cards?.[0]?.card_number,
          expiry_date: payload?.cards?.[0]?.expiry_date,
          cvv: payload?.cards?.[0]?.cvv,
        },
        {
          onSuccess: (response) => {
            toast.success("Card details updated successfully");
            setCardDetails([]); // Clear form
            refetchCardDetails(); // Refresh list
          },
          onError: (err) => {
            console.error("Error updating card info:", err);
            toast.error("Error updating card information");
          },
        }
      );
    } else {
      addCardInfo(payload, {
        onSuccess: (response) => {
          toast.success("Card details added successfully");
          setCardDetails([]); // Clear card form after successful submission
          refetchCardDetails(); // Refresh card table
        },
        onError: (err) => {
          console.error("Error adding card info:", err);
          toast.error("Error adding card information");
        },
      });
    }
  };

  // Add new card form
  const addCardForm = () => {
    setCardDetails((prev) => [
      ...prev,
      {
        card_holder_name: "",
        card_number: "",
        payment_method: "0", // Default to Credit Card
        expiry_date: "",
        cvv: "",
      },
    ]);
  };

  // Remove card form
  const removeCardForm = (index: number) => {
    setCardDetails((prev) => prev.filter((_, i) => i !== index));
  };

  // Update card field
  const updateCardField = (
    index: number,
    field: keyof CardData,
    value: string
  ) => {
    setCardDetails((prev) =>
      prev.map((card, i) => (i === index ? { ...card, [field]: value } : card))
    );
  };

  // Format card number with spaces
  const formatCardNumber = (value: string): string => {
    const cleaned = value.replace(/\s/g, "").replace(/\D/g, "");
    const matches = cleaned.match(/.{1,4}/g);
    return matches ? matches.join(" ") : cleaned;
  };

  // Validate expiry date is not in the past
  const validateExpiryDate = (date: string): boolean => {
    if (!date) return true;
    const expiry = new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return expiry >= today;
  };

  const handleBankProofChange = (e: any, typeId: any) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadDoc(
        {
          file,
          folder: String(userId),
          doc_type_id: typeId,
          user_id: String(userId) || "",
        },
        {
          onSuccess: (response: any) => {
            setBankProof(response?.uploadData?.doc_file_upload);
            refetchBankDetails();
          },
          onError: (err: any) => {
            toast.error("Something went wrong. Please try again.");
          },
        }
      );
    }
  };

  const handleQRChange = (e: any, typeId: any) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadDoc(
        {
          file,
          folder: String(userId),
          doc_type_id: typeId,
          user_id: String(userId) || "",
        },
        {
          onSuccess: (response: any) => {
            setBankQr(response?.uploadData?.doc_file_upload);
            refetchBankDetails();
          },
          onError: (err: any) => {
            toast.error("Something went wrong. Please try again.");
          },
        }
      );
    }
  };

  useEffect(() => {
    if (cardSuccess) {
      toast.success("Cards added successfully!");
      refetchCardDetails(); // Refresh card table after successful addition
    }
    if (cardErr) {
      toast.error("Failed to add cards. Please try again.");
    }
  }, [cardSuccess, cardErr]);

  return (
    <div className="w-full bg-white pb-5">
      <form
        action=""
        onSubmit={handleSubmit(onSubmit)}
        className=" flex justify-between"
      >
        <div className="bg-white/70 backdrop-blur-sm border border-white/20 overflow-hidden w-full">
          <div className="p-8 space-y-8">
            <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
              <div className="p-2 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg">
                <LandmarkIcon className="w-3 h-3 text-white" />
              </div>
              <h3 className="text-[16px] font-semibold text-gray-800">
                Bank Detail
              </h3>
            </div>
            <div className="space-y-3">
              <div className="grid md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="block text-[10px] font-medium text-gray-700">
                    Bank Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      {...register("bank_name")}
                      className="border border-gray-300 py-1 px-3 outline-none rounded-sm w-full text-[10px]"
                      placeholder="Enter Bank Name"
                    />
                    {errors?.bank_name && (
                      <p className="text-[12px] text-red-500">
                        {errors.bank_name.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="block text-[10px] font-medium text-gray-700">
                    Account Holder Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register("account_holder_name")}
                    className="border border-gray-300 py-1 px-3 outline-none rounded-sm w-full text-[10px]"
                    placeholder="Enter Account Holder Name"
                  />
                  {errors?.account_holder_name && (
                    <p className="text-[12px] text-red-500">
                      {errors.account_holder_name.message}
                    </p>
                  )}
                </div>

                <div className="space-y-1">
                  <label className="block text-[10px] font-medium text-gray-700">
                    Account No. <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    {...register("account_no")}
                    className="border border-gray-300 py-1 px-3 outline-none rounded-sm w-full text-[10px]"
                    placeholder="Enter Account No."
                  />
                  {errors?.account_no && (
                    <p className="text-[12px] text-red-500">
                      {errors.account_no.message}
                    </p>
                  )}
                </div>

                <div className="space-y-1">
                  <label className="block text-[10px] font-medium text-gray-700">
                    IFSC Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register("ifsc_code")}
                    className="border border-gray-300 py-1 px-3 outline-none rounded-sm w-full text-[10px]"
                    placeholder="Enter IFSC Code"
                  />
                  {errors?.ifsc_code && (
                    <p className="text-[12px] text-red-500">
                      {errors.ifsc_code.message}
                    </p>
                  )}
                </div>

                <div className="space-y-1">
                  <label className="block text-[10px] font-medium text-gray-700">
                    UPI ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register("upi_id")}
                    className="border border-gray-300 py-1 px-3 outline-none rounded-sm w-full text-[10px]"
                    placeholder="Enter UPI ID"
                  />
                  {errors?.upi_id && (
                    <p className="text-[12px] text-red-500">
                      {errors.upi_id.message}
                    </p>
                  )}
                </div>

                <div className="space-y-1">
                  <label className="block text-[10px] font-medium text-gray-700">
                    Branch <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register("branch")}
                    className="border border-gray-300 py-1 px-3 outline-none rounded-sm w-full text-[10px]"
                    placeholder="Enter Branch"
                  />
                  {errors?.branch && (
                    <p className="text-[12px] text-red-500">
                      {errors.branch.message}
                    </p>
                  )}
                </div>

                <div className="space-y-1">
                  <label className="block text-[10px] font-medium text-gray-700">
                    Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register("address")}
                    className="border border-gray-300 py-1 px-3 outline-none rounded-sm w-full text-[10px]"
                    placeholder="Enter Address"
                  />
                  {errors?.address && (
                    <p className="text-[12px] text-red-500">
                      {errors.address.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => reset()}
                  className="text-[10px] py-1 px-3 border border-gray-300 rounded-md shadow-sm  font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 cursor-pointer"
                >
                  Reset
                </button>
                <button
                  type="submit"
                  className="text-[10px] cursor-pointer py-1 px-3  border border-transparent rounded-md shadow-md  font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 transform hover:scale-105"
                >
                  {bankDetail ? "Update" : "Save"} Bank Details
                </button>
              </div>

              {/* Card Details Section */}
              <hr className="my-6 border-t border-gray-200" />

              <div className="flex items-center gap-3 pb-3">
                <div className="p-2 bg-gradient-to-r from-green-500 to-green-600 rounded-lg">
                  <CreditCard className="w-3 h-3 text-white" />
                </div>
                <h3 className="text-[16px] font-semibold text-gray-800">
                  Card Details
                </h3>
              </div>

              {cardDetails.map((card, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50"
                >
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-[14px] font-semibold text-gray-700">
                      Card {index + 1}
                    </h4>
                    <button
                      type="button"
                      onClick={() => removeCardForm(index)}
                      className="text-red-600 hover:bg-red-100 rounded-full cursor-pointer p-1"
                      title="Remove card"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="block text-[10px] font-medium text-gray-700">
                        Cardholder Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        value={card.card_holder_name}
                        onChange={(e) =>
                          updateCardField(
                            index,
                            "card_holder_name",
                            e.target.value
                          )
                        }
                        className="border border-gray-300 py-1 px-3 outline-none rounded-sm w-full text-[12px]"
                        placeholder="Enter Cardholder Name"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="block text-[10px] font-medium text-gray-700">
                        Card Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        value={card.card_number}
                        onChange={(e) =>
                          updateCardField(
                            index,
                            "card_number",
                            formatCardNumber(e.target.value)
                          )
                        }
                        type="text"
                        inputMode="numeric"
                        maxLength={23} // 16 digits + 3 spaces
                        className="border border-gray-300 py-1 px-3 outline-none rounded-sm w-full text-[12px]"
                        placeholder="1234 5678 9012 3456"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="block text-[10px] font-medium text-gray-700">
                        Card Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={card.payment_method}
                        onChange={(e) =>
                          updateCardField(
                            index,
                            "payment_method",
                            e.target.value
                          )
                        }
                        className="border border-gray-300 py-1 px-3 outline-none rounded-sm w-full text-[12px]"
                      >
                        <option value="0">Credit Card</option>
                        <option value="1">Debit Card</option>
                      </select>
                    </div>

                    <div className="space-y-1">
                      <label className="block text-[10px] font-medium text-gray-700">
                        Expiry Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        value={card.expiry_date}
                        onChange={(e) =>
                          updateCardField(index, "expiry_date", e.target.value)
                        }
                        type="month"
                        className={`border border-gray-300 py-1 px-3 outline-none rounded-sm w-full text-[12px] ${
                          card.expiry_date &&
                          !validateExpiryDate(card.expiry_date)
                            ? "border-red-500"
                            : ""
                        }`}
                      />
                      {card.expiry_date &&
                        !validateExpiryDate(card.expiry_date) && (
                          <p className="text-[10px] text-red-500">
                            Card has expired
                          </p>
                        )}
                    </div>

                    <div className="space-y-1">
                      <label className="block text-[10px] font-medium text-gray-700">
                        CVV <span className="text-red-500">*</span>
                      </label>
                      <input
                        value={card.cvv}
                        onChange={(e) =>
                          updateCardField(
                            index,
                            "cvv",
                            e.target.value.replace(/\D/g, "").slice(0, 4)
                          )
                        }
                        type="password"
                        inputMode="numeric"
                        maxLength={4}
                        className="border border-gray-300 py-1 px-3 outline-none rounded-sm w-full text-[12px]"
                        placeholder="123"
                      />
                    </div>
                  </div>
                </div>
              ))}

              <div className="flex justify-between items-center mt-6">
                {card == null && (
                  <button
                    type="button"
                    onClick={addCardForm}
                    className="text-[12px] bg-green-500 hover:bg-green-600 cursor-pointer text-white px-4 py-2 font-semibold rounded-md flex items-center gap-2"
                  >
                    <CreditCard className="w-3 h-3" />
                    Add Card
                  </button>
                )}

                {cardDetails.length > 0 && (
                  <button
                    type="button"
                    onClick={handleCardSubmit}
                    className="text-[12px] cursor-pointer py-2 px-4 border border-transparent rounded-md shadow-md font-medium text-white bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200"
                  >
                    {card ? "Update" : "Save"} {cardDetails.length} Card
                    {cardDetails.length > 1 ? "s" : ""}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* File upload sections */}
        <div className="flex flex-col pr-7 gap-8 py-8">
          {/* <div className="flex flex-col items-center space-y-3 mt-20 mr-10">
            <div className="relative group">
              <label className="cursor-pointer">
                <input
                  type="file"
                  onChange={(e) => {
                    handleBankProofChange(e, "2");
                  }}
                  className="hidden"
                  accept="image/*"
                />
                <div className="relative">
                  {bankProof ? (
                    <img
                      src={
                        bankProof ? `${b2bUrl}${bankProof}` : bankProofPicture
                      }
                      className="w-35 h-30 object-contain border-4 border-white shadow-md transition-all duration-300"
                      alt="Bank Proof"
                    />
                  ) : (
                    <div className="w-32 h-32 flex items-center justify-center border-3 border-dashed border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 group-hover:border-blue-400 group-hover:from-blue-50 group-hover:to-blue-100 transition-all duration-300">
                      <UploadCloud className="w-10 h-10 text-gray-400 group-hover:text-blue-500 transition-colors" />
                    </div>
                  )}
                  <div className="absolute -bottom-1 -right-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white p-2.5 rounded-full shadow-md hover:shadow-md transition-shadow">
                    <Camera className="w-4 h-4" />
                  </div>
                </div>
              </label>
            </div>
            <div className="text-center">
              <p className="text-[10px] font-medium text-gray-700">
                Bank Proof
              </p>
              <p className="text-xs text-gray-500">Click to upload or change</p>
            </div>
          </div> */}

          <ImageEditor
            image={bankProof}
            setImage={setBankProof}
            docTypeId={28}
            label="Bank Proof"
          />

          {/* <div className="flex flex-col items-center space-y-3 mt-20 mr-10">
            <div className="relative group">
              <label className="cursor-pointer">
                <input
                  type="file"
                  onChange={(e) => {
                    handleQRChange(e, "2");
                  }}
                  className="hidden"
                  accept="image/*"
                />
                <div className="relative">
                  {bankQr ? (
                    <img
                      src={bankQr ? `${b2bUrl}${bankQr}` : bankProofPicture}
                      className="w-35 h-30 object-contain border-4 border-white shadow-md transition-all duration-300"
                      alt="UPI QR"
                    />
                  ) : (
                    <div className="w-32 h-32 flex items-center justify-center border-3 border-dashed border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 group-hover:border-blue-400 group-hover:from-blue-50 group-hover:to-blue-100 transition-all duration-300">
                      <UploadCloud className="w-10 h-10 text-gray-400 group-hover:text-blue-500 transition-colors" />
                    </div>
                  )}
                  <div className="absolute -bottom-1 -right-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white p-2.5 rounded-full shadow-md hover:shadow-md transition-shadow">
                    <Camera className="w-4 h-4" />
                  </div>
                </div>
              </label>
            </div>
            <div className="text-center">
              <p className="text-[10px] font-medium text-gray-700">UPI QR</p>
              <p className="text-xs text-gray-500">Click to upload or change</p>
            </div>
          </div> */}

          <ImageEditor
            image={bankQr}
            setImage={setBankQr}
            docTypeId={29}
            label="UPI QR"
          />
        </div>
      </form>

      {/* Bank Details Table */}
      <div className="px-5 py-5">
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Bank Details
          </h3>
          <BookingTable
            data={unAssignedData}
            columns={bankDetailColumns}
            heading={[{ heading: "" }]}
            searchable={true}
            filterable={true}
            sortable={false}
          />
        </div>

        {/* Card Details Table */}
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Card Details
          </h3>
          <BookingTable
            data={cardData}
            columns={cardDetailColumns}
            heading={[{ heading: "" }]}
            searchable={true}
            filterable={true}
            sortable={false}
          />
        </div>
      </div>
    </div>
  );
}
